import React, { useEffect, useState, useContext } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import take from 'lodash/take'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import CardMedia from '@material-ui/core/CardMedia'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import StoryCardModal from './components/StoryCardModal'
import ConfirmationModal from '../../components/ConfirmationModal'
import DisabledIcon from '../Icons/DisabledIcon'
import PatientCareIcon from '../Icons/tags/PatientCareIcon'
import { useStyles } from './styles'
import { SET_STORY_VISIBILITY } from '../../network/stories/mutations'
import { TEXT_TYPE, VIDEO_TYPE, TAG_ICONS_COMPONENTS_MAP, CARD_TYPE_ICONS_MAP } from '../../constants/story'
import { SIGN_FILE_LINK } from '../../network/stories/queries'
import { Box } from '@material-ui/core'
import { LabContext } from '../../context'

const StoryCard = ({
  showMuteButton,
  card: { resourceType, thumbnailLocation, tags, name, resourceUri, dateCreated, user, id },
  linksPairs
}) => {
  const [isModalOpen, setModalOpen] = useState(false)
  const [presignedFileLink, setPresignedFileLink] = useState('')
  const [expirationTime, setExpirationTime] = useState(null)
  const [textCardContent, setTextCardContent] = useState('')
  const [confirmation, setConfirmation] = useState(false)
  const { isSuperCoordinator } = useContext(LabContext)

  const [setStoryNotVisibleToCoordinator] = useMutation(SET_STORY_VISIBILITY, { refetchQueries: ['storiesList'] })

  const [signFileLink, { loading: isLinkSigning, data: linkData, error }] = useLazyQuery(SIGN_FILE_LINK, {
    variables: {
      linkToSign: resourceUri
    },
    fetchPolicy: 'no-cache'
  })

  const getTextContent = async link => {
    const result = await fetch(link)
    const text = result.status === 404 ? 'Content was not found' : await result.text()
    setTextCardContent(text)
  }

  useEffect(() => {
    if (isModalOpen && !isLinkSigning && !error) {
      const currentTime = Date.now()
      if (!linkData || !Object.keys(linkData) || (presignedFileLink && currentTime > expirationTime)) {
        signFileLink()
      } else {
        if (!presignedFileLink) {
          setPresignedFileLink(linkData.me.security.signFileLink.preSignedLink)
          setExpirationTime(linkData.me.security.signFileLink.expiresAt)
          if (resourceType === TEXT_TYPE) {
            getTextContent(linkData.me.security.signFileLink.preSignedLink)
          }
        }
      }
    }
  }, [isModalOpen, signFileLink, isLinkSigning, linkData, presignedFileLink, resourceType, expirationTime, error])

  const handleModalOpen = () => {
    setModalOpen(true)
  }

  const handleModalClose = () => {
    setModalOpen(false)
  }

  const openConfirmationHandler = e => {
    if (e) {
      e.stopPropagation()
    }
    setConfirmation(!confirmation)
  }

  const handleUnshareStory = () => {
    const variables = { storyId: id }
    setStoryNotVisibleToCoordinator({ variables })
    openConfirmationHandler()
  }
  // const unshareStory = () => {
  //   // onUnshare(id)
  //   openConfirmationHandler()
  // }

  const classes = useStyles()
  const CardTypeIconComponent = CARD_TYPE_ICONS_MAP[resourceType]
  let mostTags = take(tags, 3)
  let tagLabelsString = mostTags.map(tag => tag.name).join(', ')
  if (tags.length > 3) {
    tagLabelsString = tagLabelsString.concat(` and ${tags.length - 3} more`)
  }

  return (
    <Grid item xs='auto'>
      <Card className={classes.card} onClick={handleModalOpen} title={name}>
        {resourceType === VIDEO_TYPE ? (
          <CardMedia
            classes={{ root: classes.cardMedia }}
            image={linksPairs && linksPairs[thumbnailLocation] ? linksPairs[thumbnailLocation].preSignedLink : ''}
            component='img'
          />
        ) : (
          <div className={classes.cardMedia} style={{ backgroundColor: resourceType === TEXT_TYPE ? '#F8F4F2' : '#E4E8F1' }}></div>
        )}
        <CardContent className={classes.cardContent}>
          <Typography variant='h6' component='div' className={classes.tagLabels}>
            {tagLabelsString}
          </Typography>
        </CardContent>
        <CardContent className={classes.cardName}>
          <Typography variant='h5' component='div' className={classes.name}>
            {name}
          </Typography>
        </CardContent>
        <div className={classes.cardTypeIcon}>
          <CardTypeIconComponent size={30} />
        </div>
        {isSuperCoordinator && showMuteButton && (
          <Box title='Mute story' className={classes.cardUnshareIcon} onClick={e => openConfirmationHandler(e)}>
            <DisabledIcon color='red' size='small' />
          </Box>
        )}
        {/* <div className={classes.bookmarkIcon}>
          <BookmarkIconFilled />
        </div> */}
        <div className={classes.tagIcons}>
          {mostTags.length ? (
            <div className={classes.tagIconsCloud}>
              {mostTags.map(tag => {
                const TagIconComponent = TAG_ICONS_COMPONENTS_MAP[tag.name] || PatientCareIcon
                return <TagIconComponent size={36} key={tag.id} />
              })}
              {tags.length > 3 ? (
                <Typography variant='h5' component='div' className={classes.moreTags}>
                  {`+ ${tags.length - 3}`}
                </Typography>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>
      <ConfirmationModal
        open={confirmation}
        content='Are you sure you want to mute this story?'
        handleClose={openConfirmationHandler}
        handleApprove={handleUnshareStory}
        approveButtonText='Mute'
        cancelButtonText='Cancel'
      />
      <StoryCardModal
        isModalOpen={isModalOpen}
        resourceType={resourceType}
        handleModalClose={handleModalClose}
        isLinkSigning={isLinkSigning}
        presignedFileLink={presignedFileLink}
        textCardContent={textCardContent}
        name={name}
        dateCreated={dateCreated}
        user={user}
        tags={tags}
      />
    </Grid>
  )
}

export default StoryCard
